import React, { FC, RefObject, useCallback, useEffect, useMemo } from 'react'
import styles from './_PayTicketOrderForm.module.scss'
import { PaymentType } from '../../../../modules/consume/domain/models/PaymentType';
import { Button, Drawer, Icon, IconButton } from '@mui/material';
import { scrooToElement } from '../../../../cross-cutting/ScrollToElement';
import { PaymentSelector } from '../paymentTypeSelector/PaymentSelector';
import { PaymentOrderResume } from './ticketOrderResume/PaymentOrderResume';
import { IOrderItem } from '../../../../modules/cart/domain/models/IOrderItem';
import ISimplePos from '../../../../modules/simplePos/model/ISimplePos';
import { SimplePosSelector } from '../SimplePosSelector/SimplePosSelector';
import UseDeviceStore from '../../../../modules/device/store/DeviceStore';
import CustomerForm from '../../../TableMap/components/customerForm/CustomerForm';
import { ICustomerFormValue } from '../../../TableMap/components/customerForm/ICustomerFormValue';
import Discount, { IDiscountValue } from '../discount/Discount';
import ObservationInput from '../observationInput/ObservationInput';
import { useI18n } from '../../../../hooks/useI18n';
import PartialPaymentOption from '../../../TableMap/components/options/PartialPaymentOption';
import { usePartialPaymentStore } from '../../../../modules/cart/infra/store/PartialPaymentStore';
import { useUi } from '../../../../modules/ui/presentation/context/UiContext';
import { SyncOrderTransactions } from '../../../../modules/prePaidTickets/domain/dtos/SyncPrePaidTickets';

export interface ICloseAccountFormProps {
    //propertys
    onClose?: () => void;
    orderItems: IOrderItem[];
    amountToPay: number;
    onSubmit: (transactions: SyncOrderTransactions[], customerValues?: ICustomerFormValue, simplePos?: ISimplePos | null, discount?: IDiscountValue, notes?: string) => void;
    simplePosList?: ISimplePos[];
    enableDiscount?: boolean;
}

const PayTicketForm: FC<ICloseAccountFormProps> = ({ onClose, onSubmit, amountToPay, orderItems, simplePosList, enableDiscount }) => {
    const { t } = useI18n();
    const { fullScreenToast } = useUi();
    const [deviceConfig, disabledSound] = UseDeviceStore(state => [state.deviceConfig, state.disbledSound]);
    const [
        paidAmount,
        setPaidAmount,
        amountToPayStore,
        setAmountToPayStore,
        totalStore,
        transactionsStore,
        setTransactionsStore
    ] = usePartialPaymentStore(state => [state.paidAmount, state.setPaidAmount, state.amountToPay, state.setAmountToPay, state.total, state.transactions, state.setTransactions]);
    
    const [simplePos, setSimplePos] = React.useState<ISimplePos | null>(null);
    const [paymentType, setPaymentType] = React.useState<PaymentType>();
    const [customerValues, setCustomerValues] = React.useState<ICustomerFormValue>();
    const [hasCustomerData, setHasCustomerData] = React.useState(false);
    const [checkCustomerData, setCheckCustomerData] = React.useState(false);
    const [discount, setDiscount] = React.useState<IDiscountValue>();
    const [notes, setNotes] = React.useState<string>();
    const [discountConfig, setDiscountConfig] = React.useState(false);
    const [partialPaymentCheck, setPartialPaymentCheck] = React.useState(false);
    const [partialPaymentInput, setPartialPaymentInput] = React.useState(0);

    const handleTicketCustomerData = useMemo(() => {
        const customerIdentification = deviceConfig?.operation.methods.find((method) => method.name === "Pré-pago Ficha")?.customerIdentification ?? [];

        if (customerIdentification?.length === 0) {
            if (deviceConfig?.invoice.emitEnabled) {
                return [{ identification: { name: "CPF", value: 2 }, enabled: true, required: false }];
            }
            return customerIdentification;
        }

        return customerIdentification.map((field) => field.identification.name === "CPF" && deviceConfig?.invoice.emitEnabled
            ? { ...field, enabled: true }
            : field
        );
    }, [deviceConfig]);

    const onClickPaymentButtonHandle = useCallback(() => {
        if (!paymentType) return;
        if (paymentType === PaymentType.cielo && simplePosList?.length && simplePos === undefined) return;
        if (!checkCustomerData && handleTicketCustomerData?.some((item) => item.enabled)) {
            return setHasCustomerData(true);
        }
        if (partialPaymentCheck && totalStore - paidAmount - partialPaymentInput > 0) {
            if (!partialPaymentInput) return;
            setPaidAmount(partialPaymentInput);
            setAmountToPayStore(totalStore - paidAmount - partialPaymentInput);
            setTransactionsStore([...transactionsStore, {
                paymentType: paymentType,
                acquirerType: 3,
                status: 3,
                amount: partialPaymentInput,
                transactionDate: new Date(),
            }]);
            fullScreenToast(t('orderFeedback.paymentSentSuccessfullyExclamation'), 'success', { sound: !disabledSound });
            return;
        }

        const transactions = [...transactionsStore, {
            paymentType: paymentType,
            acquirerType: 3,
            status: 3,
            amount: totalStore - paidAmount,
            transactionDate: new Date(),
        }];

        onSubmit(transactions, customerValues, paymentType === PaymentType.cielo ? simplePos : undefined, discount, notes);

    }, [checkCustomerData, customerValues, disabledSound, discount, fullScreenToast, handleTicketCustomerData, notes, onSubmit, paidAmount, partialPaymentCheck, partialPaymentInput, paymentType, setAmountToPayStore, setPaidAmount, setTransactionsStore, simplePos, simplePosList?.length, t, totalStore, transactionsStore]);

    const onChangePartialPayment = useCallback((value: number) => {
        setPartialPaymentInput(value);
    }, []);

    const onSubmitCustomerData = useCallback((values?: ICustomerFormValue) => {
        setCustomerValues(values);
        setCheckCustomerData(true);
        setHasCustomerData(false);
    }, []);

    const validate = useMemo(() => {
        if (!paymentType) {
            return false
        }
        if (orderItems && orderItems.length === 0) {
            return false
        }
        if (paymentType === PaymentType.cielo) {
            // if (!simplePosList?.length) {
            //     return false;
            // }
            if (simplePos === undefined) {
                return false;
            }
        }
        return true
    }, [paymentType, orderItems, simplePos]);

    useEffect(() => {
        if (simplePosList && simplePosList.length) {
            setSimplePos(simplePosList[0]);
        }
    }, [simplePosList])


    const onFocusInput = useCallback((inputEvent: RefObject<HTMLInputElement>) => {
        scrooToElement(inputEvent);
    }, [])

    if (discountConfig) {
        return (
            <Discount
                total={amountToPay}
                remaining={amountToPay}
                currentDiscount={discount}
                updateDiscount={setDiscount}
                discountConfig={discountConfig}
                setDiscountConfig={setDiscountConfig}
            />
        )
    }

    return (
        <div id={styles.PayTicketForm} >
            <div className={styles.header} >
                <IconButton onClick={discountConfig ? () => setDiscountConfig(false) : onClose} className={styles.closeButton}><Icon >close</Icon></IconButton>
                <h2>{t('paymentModal.title')}</h2>
            </div>
            <div className={styles.container} >
                <div className={styles.content} >
                    <PaymentOrderResume
                        orderItems={orderItems}
                        totalToPay={amountToPay}
                        discount={discount?.amount}
                        paidAmount={paidAmount}
                    />

                    {deviceConfig?.payment.isPaymentDivisorEnabled &&
                        <PartialPaymentOption
                            onFocusInput={onFocusInput}
                            amountToPay={amountToPay - (discount?.amount ?? 0)}
                            total={amountToPay - (discount?.amount ?? 0)}
                            onChange={onChangePartialPayment}
                            setCheckPartialPayment={setPartialPaymentCheck}
                        />
                    }

                    {amountToPay > 0 && enableDiscount && (
                        <Discount
                            total={amountToPay}
                            remaining={amountToPay}
                            currentDiscount={discount}
                            updateDiscount={setDiscount}
                            discountConfig={discountConfig}
                            setDiscountConfig={setDiscountConfig}
                        />
                    )}

                    <PaymentSelector
                        paymentType={paymentType}
                        onChangePaymentType={setPaymentType}
                        onFocusInput={onFocusInput}
                        amountToPay={amountToPay - (discount?.amount ?? 0)}
                        // showCielo={simplePosList && simplePosList.length > 0}
                        showCielo
                    />
                    {
                        paymentType === PaymentType.cielo && simplePosList && simplePosList.length > 0 &&
                        <SimplePosSelector
                            onChangeSimplePos={setSimplePos}
                            selectedSimplePos={simplePos}
                            simplePosList={simplePosList}
                        />
                    }
                </div>
                <div className={styles.footer}>
                    <ObservationInput onChange={setNotes} rows={3} />
                    <Button
                        variant={'contained'}
                        disabled={!validate}
                        size='large'
                        color='primary'
                        className={styles.button}
                        onClick={onClickPaymentButtonHandle}
                    >
                        {!amountToPayStore ? t('catalog.sendOrder') : t('paymentModal.sendPayment')}
                    </Button>
                </div>
            </div>

            <Drawer open={hasCustomerData} anchor='right' onClose={() => setHasCustomerData(false)}>
                <div>
                    <CustomerForm
                        onClose={() => setHasCustomerData(false)}
                        onSubmit={onSubmitCustomerData}
                        handleCustomerData={handleTicketCustomerData}
                        invoiceEnabled={deviceConfig?.invoice.emitEnabled}
                    />
                </div>
            </Drawer>
        </div >
    )
}
export default PayTicketForm


